import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { 
  Camera, Upload, X, CheckCircle, Clock, PlayCircle, 
  Search, Filter, User, Phone, MapPin, Calendar, MessageSquare,
  FileText, Building, ChevronLeft, ChevronRight, Eye, Download,
  RefreshCw
} from "lucide-react";

const VisitorProblems = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("visitor_name");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [visitorData, setVisitorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [remarks, setRemarks] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [selectedVisitorName, setSelectedVisitorName] = useState("");
  
  // Remark functionality
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [remarkImages, setRemarkImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedVisitorForRemark, setSelectedVisitorForRemark] = useState(null);
  
  // Office filter
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState("all");

  // Location filters
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedAssembly, setSelectedAssembly] = useState("");

  // Success/Error messages
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  // For resolve issue modal
  const [resolveImages, setResolveImages] = useState([]);
  const [resolveImagePreviews, setResolveImagePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const resolveFileInputRef = useRef(null);
  const resolveCameraInputRef = useRef(null);
  
  const perPage = 10;

  // Status mapping
  const statusConfig = {
    "open": {
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      icon: Clock,
      dotColor: "bg-yellow-500"
    },
    "on-going": {
      label: "In-Progress",
      color: "bg-blue-100 text-blue-800 border-blue-200",
      icon: PlayCircle,
      dotColor: "bg-blue-500"
    },
    "close": {
      label: "Resolved",
      color: "bg-green-100 text-green-800 border-green-200",
      icon: CheckCircle,
      dotColor: "bg-green-500"
    }
  };

  // Show message function
  const showAlertMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setShowMessage(true);
    setTimeout(() => setShowMessage(false), 5000);
  };

  // Fetch offices list
  const fetchOffices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}getOfficesList`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === "success") {
        setOffices(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching offices:", error);
      showAlertMessage("Failed to load offices", "error");
    }
  };

  // Fetch states and locations
  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: null, dist_name1: null },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setStates(response.data.states || []);
        
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      showAlertMessage("Failed to load locations", "error");
    }
  };

  // Fetch districts
  const fetchDistricts = async (state) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: state },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setDistricts(response.data.districts || []);
        setAssemblies([]);
        setSelectedDistrict("");
        setSelectedAssembly("");
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  // Fetch assemblies
  const fetchAssemblies = async (state, district) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: state, dist_name1: district },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setAssemblies(response.data.assemblies || []);
        setSelectedAssembly("");
      }
    } catch (error) {
      console.error("Error fetching assemblies:", error);
    }
  };

  // Handle state change
  const handleStateChange = (e) => {
    const state = e.target.value;
    setSelectedState(state);
    setSelectedDistrict("");
    setSelectedAssembly("");
    setDistricts([]);
    setAssemblies([]);
    
    if (state) {
      fetchDistricts(state);
    }
  };

  // Handle district change
  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setSelectedDistrict(district);
    setSelectedAssembly("");
    setAssemblies([]);
    
    if (district && selectedState) {
      fetchAssemblies(selectedState, district);
    }
  };

  // Fetch visitor data
  const fetchVisitorData = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const params = {
        status: statusFilter !== "all" ? statusFilter : "",
        start_date: startDate || "",
        end_date: endDate || "",
        page: page.toString(),
        perPage: perPage.toString(),
        search_query: searchQuery || "",
        search_type: searchType
      };

      // Add location filters if selected
      if (selectedState) {
        params.state = selectedState;
      }
      if (selectedDistrict) {
        params.district = selectedDistrict;
      }
      if (selectedAssembly) {
        params.assembly = selectedAssembly;
      }
      if (selectedOffice !== "all") {
        params.office_id = selectedOffice;
      }

      const response = await axios.post(
        `${API_URL}getReportbystatus`,
        new URLSearchParams(params).toString(),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status === "success") {
        setVisitorData(response.data.data || []);
        const totalRecords = response.data.count || 0;
        setTotalPages(Math.ceil(totalRecords / perPage));
        
        if (response.data.data.length === 0 && page > 1) {
          setCurrentPage(1);
          fetchVisitorData(1);
        }
      } else {
        setVisitorData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching visitor data:", error);
      setVisitorData([]);
      setTotalPages(1);
      showAlertMessage("Failed to load visitor data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffices();
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchVisitorData(currentPage);
  }, [currentPage, selectedOffice, statusFilter]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle image selection for remark
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + remarkImages.length > 5) {
      showAlertMessage("Maximum 5 images allowed", "error");
      return;
    }
    
    const validFiles = files.filter(file => {
      if (file.size > 200 * 1024) { // 200KB check
        showAlertMessage(`File ${file.name} exceeds 200KB limit`, "error");
        return false;
      }
      return true;
    });
    
    const newImages = [...remarkImages, ...validFiles];
    setRemarkImages(newImages);
    
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  // Handle image selection for resolve issue
  const handleResolveImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + resolveImages.length > 5) {
      showAlertMessage("Maximum 5 images allowed", "error");
      return;
    }
    
    const validFiles = files.filter(file => {
      if (file.size > 200 * 1024) { // 200KB check
        showAlertMessage(`File ${file.name} exceeds 200KB limit`, "error");
        return false;
      }
      return true;
    });
    
    const newImages = [...resolveImages, ...validFiles];
    setResolveImages(newImages);
    
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setResolveImagePreviews(prev => [...prev, ...newPreviews]);
  };

  // Handle camera capture for remark
  const handleCameraClick = () => {
    cameraInputRef.current.click();
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (remarkImages.length >= 5) {
        showAlertMessage("Maximum 5 images allowed", "error");
        return;
      }
      if (file.size > 200 * 1024) {
        showAlertMessage("Image exceeds 200KB limit", "error");
        return;
      }
      const newImages = [...remarkImages, file];
      setRemarkImages(newImages);
      
      const previewUrl = URL.createObjectURL(file);
      setImagePreviews(prev => [...prev, previewUrl]);
    }
  };

  // Handle camera capture for resolve issue
  const handleResolveCameraClick = () => {
    resolveCameraInputRef.current.click();
  };

  const handleResolveCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (resolveImages.length >= 5) {
        showAlertMessage("Maximum 5 images allowed", "error");
        return;
      }
      if (file.size > 200 * 1024) {
        showAlertMessage("Image exceeds 200KB limit", "error");
        return;
      }
      const newImages = [...resolveImages, file];
      setResolveImages(newImages);
      
      const previewUrl = URL.createObjectURL(file);
      setResolveImagePreviews(prev => [...prev, previewUrl]);
    }
  };

  // Remove remark image
  const removeImage = (index) => {
    URL.revokeObjectURL(imagePreviews[index]);
    setRemarkImages(remarkImages.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  // Remove resolve image
  const removeResolveImage = (index) => {
    URL.revokeObjectURL(resolveImagePreviews[index]);
    setResolveImages(resolveImages.filter((_, i) => i !== index));
    setResolveImagePreviews(resolveImagePreviews.filter((_, i) => i !== index));
  };

  // Handle remark submission
  const handleRemarkSubmit = async () => {
    if (!remarkText.trim() && remarkImages.length === 0) {
      showAlertMessage("Please enter a remark or upload an image", "error");
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      
      formData.append("visitor_id", selectedVisitorForRemark);
      formData.append("remark", remarkText);
      
      remarkImages.forEach((image, index) => {
        formData.append(`remark_image[${index}]`, image);
      });

      const response = await axios.post(
        `${API_URL}insertRemark`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        showAlertMessage("Remark added successfully!");
        fetchVisitorData(currentPage);
        resetRemarkModal();
      } else {
        showAlertMessage("Failed to add remark", "error");
      }
    } catch (error) {
      console.error("Error submitting remark:", error);
      showAlertMessage("An error occurred while adding remark", "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Reset remark modal
  const resetRemarkModal = () => {
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setShowRemarkModal(false);
    setRemarkText("");
    setRemarkImages([]);
    setImagePreviews([]);
    setSelectedVisitorForRemark(null);
  };

  // Reset resolve modal
  const resetResolveModal = () => {
    resolveImagePreviews.forEach(url => URL.revokeObjectURL(url));
    setShowPopup(false);
    setRemarks("");
    setResolveImages([]);
    setResolveImagePreviews([]);
    setSelectedVisitor(null);
    setSelectedVisitorName("");
  };

  // Open remark modal
  const openRemarkModal = (visitorId, visitorName) => {
    setSelectedVisitorForRemark(visitorId);
    setSelectedVisitorName(visitorName);
    setShowRemarkModal(true);
  };

  // Open resolve modal
  const openResolveModal = (visitorId, visitorName) => {
    setSelectedVisitor(visitorId);
    setSelectedVisitorName(visitorName);
    setShowPopup(true);
  };

  // Handle status update (resolve issue)
  const handleStatusUpdate = async () => {
    if (!selectedVisitor || !remarks.trim()) {
      showAlertMessage("Please enter resolution remarks", "error");
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      
      formData.append("visitor_id", selectedVisitor);
      formData.append("remarks", remarks);
      
      resolveImages.forEach((image, index) => {
        formData.append(`close_remark_image[${index}]`, image);
      });

      const response = await axios.post(
        `${API_URL}updateStatus`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        showAlertMessage("Visitor status updated successfully!");
        fetchVisitorData(currentPage);
        resetResolveModal();
      } else {
        showAlertMessage("Failed to update visitor status", "error");
      }
    } catch (error) {
      console.error("Error updating visitor status:", error);
      showAlertMessage("An error occurred while updating status", "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    fetchVisitorData(1);
  };

  // Handle reset
  const handleReset = () => {
    setSearchQuery("");
    setSearchType("visitor_name");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
    setSelectedOffice("all");
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedAssembly("");
    setDistricts([]);
    setAssemblies([]);
    setCurrentPage(1);
    fetchVisitorData(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    const maxButtons = 5;
    
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      if (currentPage <= 3) {
        buttons.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        buttons.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        buttons.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return buttons;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
      {/* Success/Error Message */}
      {showMessage && (
        <div className={`fixed top-4 right-4 z-50 animate-slideIn ${messageType === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white px-6 py-4 rounded-lg shadow-xl flex items-center gap-3`}>
          {messageType === 'success' ? (
            <CheckCircle className="w-6 h-6" />
          ) : (
            <X className="w-6 h-6" />
          )}
          <div>
            <p className="font-semibold">{message}</p>
          </div>
          <button 
            onClick={() => setShowMessage(false)}
            className="ml-4 text-white hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Visitor Problems</h1>
            <p className="text-gray-600 mt-2">Manage and track visitor issues efficiently</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => fetchVisitorData(currentPage)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Issues</p>
                <p className="text-2xl font-bold text-gray-800">
                  {visitorData.filter(v => v.status === 'open').length}
                </p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-2xl font-bold text-gray-800">
                  {visitorData.filter(v => v.status === 'on-going').length}
                </p>
              </div>
              <PlayCircle className="w-10 h-10 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Resolved</p>
                <p className="text-2xl font-bold text-gray-800">
                  {visitorData.filter(v => v.status === 'close').length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Issues</p>
                <p className="text-2xl font-bold text-gray-800">{visitorData.length}</p>
              </div>
              <FileText className="w-10 h-10 text-purple-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-xl font-bold text-gray-800">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Search By</label>
            <div className="flex gap-2">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="w-1/3 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="visitor_name">Name</option>
                <option value="mobile_number">Mobile</option>
                <option value="traking_id">Tracking ID</option>
              </select>
              <input
                type="text"
                placeholder={`Search by ${searchType === 'visitor_name' ? 'name' : searchType === 'mobile_number' ? 'mobile' : 'tracking ID'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="open">Pending</option>
              <option value="on-going">In Progress</option>
              <option value="close">Resolved</option>
            </select>
          </div>

          {/* Office Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Office</label>
            <select
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Offices</option>
              {offices.map((office) => (
                <option key={office.id} value={office.id}>
                  {office.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Date Range</label>
            <div className="flex gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* State Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">State</label>
            <select
              value={selectedState}
              onChange={handleStateChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">All States</option>
              {states.map((state, index) => (
                <option key={index} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          {/* District Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">District</label>
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              disabled={!selectedState}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">All Districts</option>
              {districts.map((district, index) => (
                <option key={index} value={district}>
                  {district}
                </option>
              ))}
            </select>
          </div>

          {/* Assembly Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Assembly</label>
            <select
              value={selectedAssembly}
              onChange={(e) => setSelectedAssembly(e.target.value)}
              disabled={!selectedDistrict}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">All Assemblies</option>
              {assemblies.map((assembly, index) => (
                <option key={index} value={assembly}>
                  {assembly}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleSearch}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-medium flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <Search className="w-5 h-5" />
            Search
          </button>
          <button
            onClick={handleReset}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium flex items-center gap-2"
          >
            <X className="w-5 h-5" />
            Clear Filters
          </button>
        </div>
      </div>

      {/* Visitor Data Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Visitor Issues</h2>
              <p className="text-gray-600 text-sm">Showing {visitorData.length} records</p>
            </div>
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Loading visitor data...</p>
          </div>
        ) : visitorData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Visitor Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Contact & Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Query Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visitorData.map((visitor) => {
                  const status = statusConfig[visitor.status];
                  const StatusIcon = status?.icon;
                  
                  return (
                    <tr 
                      key={visitor.id} 
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status.color.replace('bg-', 'bg-').replace('text-', 'text-')}`}>
                            <User className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {visitor.visitor_name}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                              <span className="font-medium">ID:</span> {visitor.visitor_id}
                            </div>
                            <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(visitor.created_at)}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{visitor.mobile_number}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {visitor.state || "N/A"}
                              {visitor.district && `, ${visitor.district}`}
                              {visitor.assembly && `, ${visitor.assembly}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {visitor.office?.name || "No Office"}
                            </span>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          {visitor.query ? (
                            <div className="group">
                              <div className="flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                                <p className="text-sm text-gray-700 line-clamp-2">
                                  {visitor.query}
                                </p>
                              </div>
                              <button
                                onClick={() => setSelectedQuery(visitor)}
                                className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                              >
                                <Eye className="w-3 h-3" />
                                View Details
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic text-sm">No query</span>
                          )}
                          
                          {/* Remarks count */}
                          {visitor.remarks && visitor.remarks.length > 0 && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700">
                                {visitor.remarks.length} remark{visitor.remarks.length > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          {status ? (
                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${status.color}`}>
                              <div className={`w-2 h-2 rounded-full ${status.dotColor}`}></div>
                              {StatusIcon && <StatusIcon className="w-4 h-4" />}
                              {status.label}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border bg-gray-100 text-gray-800 border-gray-200">
                              <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                              Unknown
                            </span>
                          )}
                          
                          {visitor.problem_resolvedate && (
                            <div className="text-xs text-gray-500">
                              Resolved: {formatDate(visitor.problem_resolvedate)}
                            </div>
                          )}
                          
                          {visitor.traking_id && (
                            <div className="text-xs text-gray-600 font-mono">
                              Track: {visitor.traking_id}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          {/* Add Remark Button - Enabled for open and on-going */}
                          {(visitor.status === 'open' || visitor.status === 'on-going') && (
                            <button
                              onClick={() => openRemarkModal(visitor.visitor_id, visitor.visitor_name)}
                              className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 text-sm font-medium shadow-sm hover:shadow"
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Add Remark
                            </button>
                          )}
                          
                          {/* Resolve Button - Only for open and on-going */}
                          {(visitor.status === 'open' || visitor.status === 'on-going') && (
                            <button
                              onClick={() => openResolveModal(visitor.visitor_id, visitor.visitor_name)}
                              className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 text-sm font-medium shadow-sm hover:shadow"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Resolve Issue
                            </button>
                          )}
                          
                          {/* Disabled buttons for resolved status */}
                          {visitor.status === 'close' && (
                            <div className="space-y-2">
                              <button
                                disabled
                                className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Remark Added
                              </button>
                              <button
                                disabled
                                className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Issue Resolved
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 text-gray-300">
              <FileText className="w-full h-full" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Records Found</h3>
            <p className="text-gray-500">Try adjusting your search filters or date range</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                Showing {visitorData.length} of {visitorData.length * currentPage} records
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-1">
                  {renderPaginationButtons().map((page, index) => (
                    <button
                      key={index}
                      onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
                      className={`w-10 h-10 rounded-lg font-medium ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : page === '...'
                          ? 'text-gray-400 cursor-default'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Remark Modal */}
      {showRemarkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Add Remark</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Visitor: <span className="font-semibold">{selectedVisitorName}</span>
                  </p>
                </div>
                <button
                  onClick={resetRemarkModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remark Text <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    placeholder="Enter detailed remarks here..."
                    rows="4"
                    className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none shadow-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Provide detailed information about the progress or resolution
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Images (Optional)
                      </label>
                      <p className="text-xs text-gray-500">Maximum 5 images, 200KB each</p>
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {remarkImages.length}/5
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm font-medium text-gray-700">Upload Files</span>
                      <span className="text-xs text-gray-500">JPG, PNG, GIF</span>
                    </button>
                    
                    <button
                      onClick={handleCameraClick}
                      className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                    >
                      <Camera className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm font-medium text-gray-700">Take Photo</span>
                      <span className="text-xs text-gray-500">Use Camera</span>
                    </button>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <input
                    type="file"
                    ref={cameraInputRef}
                    onChange={handleCameraCapture}
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                  />

                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="grid grid-cols-3 gap-3">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border shadow-sm"
                          />
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={resetRemarkModal}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemarkSubmit}
                  disabled={isUploading || (!remarkText.trim() && remarkImages.length === 0)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Submit Remark
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Issue Modal */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Resolve Issue</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Visitor: <span className="font-semibold">{selectedVisitorName}</span>
                  </p>
                </div>
                <button
                  onClick={resetResolveModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resolution Remarks <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Describe how the issue was resolved..."
                    rows="4"
                    className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none shadow-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    This will mark the issue as resolved and notify the visitor
                  </p>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Images (Optional)
                      </label>
                      <p className="text-xs text-gray-500">Maximum 5 images, 200KB each</p>
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {resolveImages.length}/5
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      onClick={() => resolveFileInputRef.current.click()}
                      className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-300 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all duration-300"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm font-medium text-gray-700">Upload Files</span>
                      <span className="text-xs text-gray-500">JPG, PNG, GIF</span>
                    </button>
                    
                    <button
                      onClick={handleResolveCameraClick}
                      className="flex flex-col items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white border-2 border-dashed border-gray-300 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all duration-300"
                    >
                      <Camera className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm font-medium text-gray-700">Take Photo</span>
                      <span className="text-xs text-gray-500">Use Camera</span>
                    </button>
                  </div>

                  <input
                    type="file"
                    ref={resolveFileInputRef}
                    onChange={handleResolveImageSelect}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <input
                    type="file"
                    ref={resolveCameraInputRef}
                    onChange={handleResolveCameraCapture}
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                  />

                  {/* Image Previews */}
                  {resolveImagePreviews.length > 0 && (
                    <div className="grid grid-cols-3 gap-3">
                      {resolveImagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border shadow-sm"
                          />
                          <button
                            onClick={() => removeResolveImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={resetResolveModal}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={isUploading || !remarks.trim()}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Mark as Resolved
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Query Details Modal */}
      {selectedQuery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Query Details</h3>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-sm font-medium text-gray-700">
                      Visitor: {selectedQuery.visitor_name}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig[selectedQuery.status]?.color}`}>
                      {statusConfig[selectedQuery.status]?.label}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedQuery(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Query Content</h4>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedQuery.query || "No query available"}
                  </p>
                </div>
                
                {/* Remarks Section */}
                {selectedQuery.remarks && selectedQuery.remarks.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Remarks History</h4>
                    <div className="space-y-4">
                      {selectedQuery.remarks.map((remark, index) => (
                        <div key={remark.id} className="border-l-4 border-blue-500 pl-4 py-3">
                          <div className="flex justify-between items-start mb-2">
                            <p className="text-gray-700">{remark.remark}</p>
                            <span className="text-xs text-gray-500">
                              {formatDate(remark.created_at)}
                            </span>
                          </div>
                          
                          {remark.images && remark.images.length > 0 && (
                            <div className="grid grid-cols-2 gap-2 mt-3">
                              {remark.images.map((image) => (
                                <a
                                  key={image.id}
                                  href={image.image_path}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img
                                    src={image.image_path}
                                    alt="Remark attachment"
                                    className="w-full h-32 object-cover rounded-lg border hover:opacity-90 transition-opacity"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setSelectedQuery(null)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorProblems;